import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  Shield, Smartphone, Monitor, KeyRound, Download, Trash2, LogOut,
  ShieldCheck, ShieldAlert, Info, Check, X, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "sessions", label: "Sessões ativas", icon: Monitor },
  { key: "password", label: "Senha", icon: KeyRound },
  { key: "lgpd",     label: "Privacidade (LGPD)", icon: Shield },
];

export default function Security() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");
  const [tab, setTab] = useState(
    TABS.some((t) => t.key === initialTab) ? initialTab : "sessions",
  );
  const { t } = useI18n();

  useEffect(() => {
    if (initialTab && TABS.some((item) => item.key === initialTab)) {
      setTab(initialTab);
    }
  }, [initialTab]);

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="card-soft px-5 py-4">
        <div className="text-xs text-[var(--text-2)] font-medium">Segurança</div>
        <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Central de Segurança</h1>
        <p className="text-xs text-[var(--text-2)] mt-1">
          Sessões ativas, alteração de senha e direitos LGPD.
        </p>
      </div>

      <div className="card-soft p-1.5 flex items-center gap-1 overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 h-10 px-3 rounded-2xl text-xs font-semibold whitespace-nowrap transition-colors ${
              tab === key
                ? "bg-[var(--brand)] text-white shadow-sm"
                : "text-[var(--text-2)] hover:bg-[var(--surface-2)]"
            }`}
            data-testid={`sec-tab-${key}`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "sessions" && <SessionsTab />}
      {tab === "password" && <PasswordTab />}
      {tab === "lgpd" && <LgpdTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------
function SessionsTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/security/sessions");
    setItems(data);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = async (id) => {
    if (!confirm("Encerrar esta sessão?")) return;
    await api.delete(`/security/sessions/${id}`);
    toast.success("Sessão encerrada");
    load();
  };

  const revokeAll = async () => {
    if (!confirm("Encerrar TODAS as outras sessões (mantém apenas esta)?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/security/sessions/revoke_all");
      toast.success(`${data.revoked} sessão(ões) encerrada(s)`);
      load();
    } finally { setBusy(false); }
  };

  const active = items.filter((s) => !s.revoked);
  const revoked = items.filter((s) => s.revoked);

  return (
    <div className="space-y-4">
      <div className="card-soft p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="font-display text-lg font-bold">Sessões ativas ({active.length})</div>
            <div className="text-xs text-[var(--text-2)]">Dispositivos com login ativo em sua conta</div>
          </div>
          {active.length > 1 && (
            <Button onClick={revokeAll} disabled={busy} className="rounded-xl bg-[var(--danger)] hover:bg-red-600 h-10 text-xs font-semibold px-4" data-testid="revoke-all-btn">
              <LogOut size={13} className="mr-1.5" /> Encerrar outras
            </Button>
          )}
        </div>

        <div className="space-y-2">
          {active.length === 0 && (
            <div className="text-center py-8 text-sm text-[var(--text-2)]">Nenhuma sessão ativa</div>
          )}
          {active.map((s) => (
            <SessionRow key={s.id} s={s} onRevoke={() => revoke(s.id)} />
          ))}
        </div>
      </div>

      {revoked.length > 0 && (
        <div className="card-soft p-5">
          <div className="font-display text-base font-bold mb-3 text-[var(--text-2)]">Histórico de sessões encerradas</div>
          <div className="space-y-2">
            {revoked.slice(0, 10).map((s) => (
              <SessionRow key={s.id} s={s} onRevoke={null} muted />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SessionRow({ s, onRevoke, muted }) {
  const ua = s.user_agent || "";
  const isMobile = /Mobile|Android|iPhone|iPad/i.test(ua);
  const Device = isMobile ? Smartphone : Monitor;
  const parts = parseUA(ua);
  const when = new Date(s.created_at).toLocaleString("pt-BR", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });

  return (
    <div className={`rounded-2xl p-3 flex items-center gap-3 ${muted ? "bg-[var(--surface-2)]" : "bg-[var(--surface-2)]"}`} data-testid={`session-row-${s.id}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
        s.is_current
          ? "bg-[var(--brand)] text-white"
          : muted
            ? "bg-white text-[var(--text-3)]"
            : "bg-white text-[var(--brand)]"
      }`}>
        <Device size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-sm truncate">{parts.browser} · {parts.os}</div>
          {s.is_current && <span className="pill pill-success">Este dispositivo</span>}
          {muted && <span className="pill pill-neutral">{s.rotation_reason || "encerrada"}</span>}
        </div>
        <div className="text-xs text-[var(--text-2)] font-mono mt-0.5 truncate">
          IP {s.ip} · {when}
        </div>
      </div>
      {onRevoke && !s.is_current && (
        <button onClick={onRevoke} className="inline-flex items-center gap-1 px-2.5 h-8 rounded-xl bg-white text-[var(--text-2)] hover:bg-[var(--danger-soft)] hover:text-[var(--danger)] text-xs font-semibold" data-testid={`revoke-${s.id}`}>
          <LogOut size={12} /> Encerrar
        </button>
      )}
    </div>
  );
}

function parseUA(ua) {
  const os = /Windows/i.test(ua) ? "Windows"
    : /Mac OS/i.test(ua) ? "macOS"
    : /Android/i.test(ua) ? "Android"
    : /iPhone|iPad|iOS/i.test(ua) ? "iOS"
    : /Linux/i.test(ua) ? "Linux"
    : "—";
  const browser = /Edg/i.test(ua) ? "Edge"
    : /Chrome/i.test(ua) ? "Chrome"
    : /Firefox/i.test(ua) ? "Firefox"
    : /Safari/i.test(ua) ? "Safari"
    : ua.split(" ")[0] || "Desconhecido";
  return { os, browser };
}

// ---------------------------------------------------------------------------
// Password
// ---------------------------------------------------------------------------
function PasswordTab() {
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [rules, setRules] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pw) { setRules({}); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/security/password/validate", { password: pw });
        setRules(data);
      } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [pw]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { toast.error("Confirmação não confere"); return; }
    setBusy(true);
    try {
      await api.post("/security/password", { current_password: cur, new_password: pw });
      toast.success("Senha alterada. Outras sessões foram encerradas.");
      setCur(""); setPw(""); setConfirm("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail :
                  detail?.password ? detail.password.join(", ") : "Falha";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const check = (ok) => ok
    ? <Check size={14} className="text-[var(--success-text)]" />
    : <X size={14} className="text-[var(--text-3)]" />;

  const has = (key) => {
    if (!pw) return false;
    const map = {
      min_length: pw.length >= 8,
      require_upper: /[A-Z]/.test(pw),
      require_lower: /[a-z]/.test(pw),
      require_digit: /\d/.test(pw),
    };
    return map[key];
  };

  return (
    <form onSubmit={submit} className="card-soft p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[var(--brand-soft)] text-[var(--brand)] flex items-center justify-center shrink-0">
          <KeyRound size={20} />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold">Alterar senha</div>
          <div className="text-xs text-[var(--text-2)]">Ao trocar a senha, outras sessões serão encerradas automaticamente.</div>
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-[var(--text-2)]">Senha atual</label>
        <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required className="rounded-xl h-10 mt-1" data-testid="pw-current" />
      </div>
      <div>
        <label className="text-xs font-medium text-[var(--text-2)]">Nova senha</label>
        <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required className="rounded-xl h-10 mt-1" data-testid="pw-new" />
      </div>
      <div className="rounded-xl bg-[var(--surface-2)] p-3 space-y-1.5">
        {[
          ["min_length", "Mínimo 8 caracteres"],
          ["require_upper", "Ao menos 1 letra maiúscula"],
          ["require_lower", "Ao menos 1 letra minúscula"],
          ["require_digit", "Ao menos 1 número"],
        ].map(([k, label]) => (
          <div key={k} className="flex items-center gap-2 text-xs">
            {check(has(k))}
            <span className={has(k) ? "text-[var(--text-1)]" : "text-[var(--text-2)]"}>{label}</span>
          </div>
        ))}
      </div>
      <div>
        <label className="text-xs font-medium text-[var(--text-2)]">Confirme a nova senha</label>
        <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="rounded-xl h-10 mt-1" data-testid="pw-confirm" />
      </div>

      <Button type="submit" disabled={busy || !rules.ok || pw !== confirm} className="w-full rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-11" data-testid="pw-submit">
        {busy ? "Salvando..." : "Alterar senha"}
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// LGPD
// ---------------------------------------------------------------------------
function LgpdTab() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [forgetOpen, setForgetOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const exportData = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/security/lgpd/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nafe_lgpd_${user?.email}_${Date.now()}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Seus dados foram exportados");
    } catch { toast.error("Falha ao exportar"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="card-soft p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-[var(--success-soft)] text-[var(--success-text)] flex items-center justify-center shrink-0">
            <ShieldCheck size={20} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-bold">Portabilidade de dados</div>
            <p className="text-xs text-[var(--text-2)] mt-1">
              Baixe um arquivo JSON com todos os dados pessoais que a plataforma possui sobre você — perfil, documentos, evidências, ocorrências, trilha de auditoria e histórico de sessões.
            </p>
          </div>
        </div>
        <Button onClick={exportData} disabled={busy} className="rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-10 text-xs font-semibold px-4" data-testid="lgpd-export">
          <Download size={13} className="mr-1.5" /> {busy ? "Preparando..." : "Baixar meus dados"}
        </Button>
      </div>

      <div className="card-soft p-5 border-l-4 border-[var(--danger)]">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-[var(--danger-soft)] text-[var(--danger)] flex items-center justify-center shrink-0">
            <ShieldAlert size={20} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-bold text-[var(--danger-text)]">Direito ao esquecimento</div>
            <p className="text-xs text-[var(--text-2)] mt-1">
              Anonimiza permanentemente sua conta. Seu nome, e-mail e histórico de sessões serão removidos. Registros operacionais mantidos por obrigação legal serão desassociados da sua identidade.
            </p>
            <div className="mt-3 rounded-xl bg-[var(--warning-soft)] text-[var(--warning-text)] p-3 flex items-start gap-2 text-xs">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>Esta ação é irreversível. Você será deslogado imediatamente.</span>
            </div>
          </div>
        </div>
        <Button onClick={() => setForgetOpen(true)} className="rounded-xl bg-[var(--danger)] hover:bg-red-600 h-10 text-xs font-semibold px-4" data-testid="lgpd-forget-open">
          <Trash2 size={13} className="mr-1.5" /> Solicitar anonimização
        </Button>
      </div>

      <div className="card-soft p-5">
        <div className="flex items-start gap-3">
          <Info size={16} className="text-[var(--text-2)] mt-0.5 shrink-0" />
          <div className="text-xs text-[var(--text-2)] leading-relaxed">
            Nafe Logistica segue a <span className="font-semibold text-[var(--text-1)]">Lei Geral de Proteção de Dados (LGPD, Lei 13.709/2018)</span>.
            Todos os dados sensíveis (CPF/CNPJ) são armazenados criptografados em repouso (Fernet AES-128).
            Trilha de auditoria imutável é preservada por obrigação legal, mas referências ao seu ID pessoal são anonimizadas quando você exerce seu direito ao esquecimento.
            {" "}
            <a href="/privacidade" target="_blank" rel="noreferrer" className="text-[var(--brand)] font-semibold underline underline-offset-2">
              Ler a Política de Privacidade pública
            </a>.
          </div>
        </div>
      </div>

      <LgpdForgetDialog open={forgetOpen} onOpenChange={setForgetOpen} email={user?.email} onForgotten={logout} />
    </div>
  );
}

function LgpdForgetDialog({ open, onOpenChange, email, onForgotten }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (typed.toLowerCase() !== (email || "").toLowerCase()) {
      toast.error("Digite seu e-mail para confirmar"); return;
    }
    setBusy(true);
    try {
      await api.post("/security/lgpd/forget", { confirm_email: typed });
      toast.success("Conta anonimizada. Você será deslogado.");
      setTimeout(() => { onForgotten?.(); }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="lgpd-forget-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-[var(--danger-text)]">
            Confirmar anonimização
          </DialogTitle>
          <DialogDescription className="text-xs">
            Para confirmar, digite seu e-mail completo abaixo. Esta operação é <span className="font-semibold">irreversível</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-2xl bg-[var(--surface-2)] p-3 text-xs">
          <span className="text-[var(--text-2)]">Seu e-mail:</span> <span className="font-mono font-semibold">{email}</span>
        </div>
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Digite seu e-mail" className="rounded-xl h-10" data-testid="lgpd-confirm-input" />
        <div className="flex gap-2">
          <button onClick={() => onOpenChange(false)} className="flex-1 h-11 rounded-xl bg-[var(--surface-2)] hover:bg-[var(--surface-hover)] text-xs font-semibold" data-testid="lgpd-forget-cancel">
            Cancelar
          </button>
          <Button onClick={submit} disabled={busy || typed.toLowerCase() !== (email || "").toLowerCase()} className="flex-1 rounded-xl bg-[var(--danger)] hover:bg-red-600 h-11 text-xs font-semibold" data-testid="lgpd-forget-submit">
            {busy ? "Anonimizando..." : "Confirmar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
